import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MessageSquare, TrendingUp, Calendar, RefreshCw,
  Search, Briefcase, ExternalLink, AlertCircle,
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import AdminSidebar from '../components/admin/AdminSidebar';
import StatCard from '../components/admin/StatCard';
import InquiriesTable from '../components/admin/InquiriesTable';
import DealerAppsTable from '../components/admin/DealerAppsTable';
import ContentEditor from '../components/admin/ContentEditor';
import ProductsEditor from '../components/admin/ProductsEditor';
import GalleryEditor from '../components/admin/GalleryEditor';
import TestimonialsEditor from '../components/admin/TestimonialsEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const { user, loading: authLoading, logout, checkSession } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [dealerApps, setDealerApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    setError('');
    try {
      const opts = { withCredentials: true };
      const [inquiryRes, dealerRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/inquiries`, opts),
        axios.get(`${BACKEND_URL}/api/admin/dealer-applications`, opts),
        axios.get(`${BACKEND_URL}/api/admin/stats`, opts),
      ]);
      setInquiries(inquiryRes.data);
      setDealerApps(dealerRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate('/admin/login');
      } else {
        setError('Failed to load data. Please refresh.');
      }
    } finally {
      setDataLoading(false);
    }
  }, [logout, navigate]);

  const recheckedSession = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      fetchData();
    } else if (!recheckedSession.current) {
      // Cookie may still be valid after client-side navigation from a public page
      recheckedSession.current = true;
      checkSession().then((u) => { if (!u) navigate('/admin/login'); });
    } else {
      navigate('/admin/login');
    }
  }, [authLoading, user, navigate, fetchData, checkSession]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const updateInquiryStatus = useCallback(async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/inquiries/${id}/status`, { status }, { withCredentials: true });
      setInquiries((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (err) {
      setError('Failed to update status. Please try again.');
    }
  }, []);

  const updateDealerStatus = useCallback(async (id, status) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/dealer-applications/${id}/status`, { status }, { withCredentials: true });
      setDealerApps((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (err) {
      setError('Failed to update status. Please try again.');
    }
  }, []);

  const filteredInquiries = useMemo(() => {
    const q = search.toLowerCase();
    return inquiries.filter((r) =>
      !q || r.name?.toLowerCase().includes(q) || r.phone?.includes(q) || r.product?.toLowerCase().includes(q) || r.state?.toLowerCase().includes(q)
    );
  }, [inquiries, search]);

  const filteredDealers = useMemo(() => {
    const q = search.toLowerCase();
    return dealerApps.filter((r) =>
      !q || r.name?.toLowerCase().includes(q) || r.phone?.includes(q) || r.business_name?.toLowerCase().includes(q) || r.state?.toLowerCase().includes(q)
    );
  }, [dealerApps, search]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#0A3F22', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: '#6B7280' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F0F4F0' }}>
      <AdminSidebar
        tab={tab}
        onTabChange={setTab}
        inquiryCount={inquiries.length}
        dealerCount={dealerApps.length}
        user={user}
        onLogout={handleLogout}
      />

      <main className="flex-1 overflow-auto">
        <div className="bg-white border-b px-8 py-5 flex items-center justify-between sticky top-0 z-10" style={{ borderColor: '#E5E7EB' }}>
          <div>
            <h1 className="font-heading font-bold text-xl" style={{ color: '#0A3F22' }}>
              {tab === 'inquiries' && 'Product Inquiries'}
              {tab === 'dealers' && 'Dealer Applications'}
              {tab === 'content' && 'Site Content Editor'}
              {tab === 'products' && 'Products Editor'}
              {tab === 'gallery' && 'Gallery Editor'}
              {tab === 'testimonials' && 'Testimonials Editor'}
            </h1>
            <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>
              {['content', 'products', 'gallery', 'testimonials'].includes(tab)
                ? 'Edit your website — saves live instantly'
                : 'Manage leads from website forms'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-data-btn"
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
              style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}
            >
              <RefreshCw size={14} className={dataLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <a
              href="/"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
              style={{ backgroundColor: '#0A3F22', color: '#fff' }}
            >
              <ExternalLink size={14} />
              View Site
            </a>
          </div>
        </div>

        <div className="p-8">
          {error && (
            <div className="flex items-center gap-2 p-4 rounded-xl mb-6 text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {stats && !['content', 'products', 'gallery', 'testimonials'].includes(tab) && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={TrendingUp} label="Total Leads" value={stats.total_leads} sub={stats.today_leads} color="#0A3F22" />
              <StatCard icon={MessageSquare} label="Inquiries" value={stats.total_inquiries} sub={stats.today_inquiries} color="#1B6B3D" />
              <StatCard icon={Briefcase} label="Dealer Apps" value={stats.total_dealer_applications} sub={stats.today_dealer_applications} color="#D97706" />
              <StatCard icon={Calendar} label="Today's Total" value={stats.today_leads} color="#2563EB" />
            </div>
          )}

          {!['content', 'products', 'gallery', 'testimonials'].includes(tab) && (
            <div className="relative mb-5">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
              <input
                data-testid="admin-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, product, state..."
                className="w-full pl-11 pr-4 py-3 bg-white border rounded-xl text-sm outline-none transition-all"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
          )}

          {tab === 'inquiries' && (
            <InquiriesTable rows={filteredInquiries} loading={dataLoading} search={search} onStatusChange={updateInquiryStatus} />
          )}
          {tab === 'dealers' && (
            <DealerAppsTable rows={filteredDealers} loading={dataLoading} search={search} onStatusChange={updateDealerStatus} />
          )}
          {tab === 'content' && <ContentEditor />}
          {tab === 'products' && <ProductsEditor />}
          {tab === 'gallery' && <GalleryEditor />}
          {tab === 'testimonials' && <TestimonialsEditor />}
        </div>
      </main>
    </div>
  );
}
